import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { EmptyState, Panel, RouteLine, Skeleton, StatusBadge, T, humanActivity, isAuditEvent, money, timeAgo } from "@/components/admin/ui";

function minutesUntil(date, time) {
  if (!date || !time) return null;
  const target = new Date(`${date}T${time}`);
  const diff = Math.round((target.getTime() - Date.now()) / 60000);
  return Number.isFinite(diff) ? diff : null;
}

function departureLabel(min) {
  if (min === null) return null;
  if (min < 0) return "heure passée";
  if (min < 60) return `départ dans ${min} min`;
  if (min < 60 * 24) return `départ dans ${Math.round(min / 60)} h`;
  return `dans ${Math.round(min / 60 / 24)} j`;
}

export default function AdminOverview() {
  const { user } = useOutletContext() || {};
  const [data, setData] = useState(null);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [showAbandoned, setShowAbandoned] = useState(false);

  useEffect(() => {
    const refresh = () => adminApi.get("/overview").then((r) => setData(r.data)).catch(() => {});
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, []);

  if (!data) return <Skeleton rows={5} />;

  const firstName = (user?.name || "").split(" ")[0] || "";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Bonjour" : hour < 19 ? "Bon après-midi" : "Bonsoir";
  const dateLine = new Date().toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long" });

  const { today, finance, alerts, activity, upcoming, unassigned, abandoned } = data;

  // priorités : urgent = départ < 2 h ; attention = documents, échéances ; action future = sans chauffeur lointain
  // NB : les paiements échoués (réservations abandonnées) ne sont PAS des actions — section discrète plus bas.
  const enriched = unassigned.map((b) => {
    const min = minutesUntil(b.date, b.time);
    return { kind: "unassigned", booking: b, min, level: min !== null && min <= 120 ? "critical" : min !== null && min <= 60 * 24 ? "attention" : "upcoming" };
  });
  const otherAlerts = alerts.filter((a) => !["payment_failed", "unassigned"].includes(a.type)).map((a) => ({ kind: "alert", alert: a, level: a.level === "high" ? "critical" : "attention" }));
  const all = [...enriched, ...otherAlerts]
    .sort((a, b2) => ({ critical: 0, attention: 1, upcoming: 2 })[a.level] - ({ critical: 0, attention: 1, upcoming: 2 })[b2.level]
      || (a.min ?? 9999) - (b2.min ?? 9999));
  const visible = showAllAlerts ? all : all.slice(0, 5);
  const criticalCount = all.filter((a) => a.level === "critical").length;
  const attentionTotal = today.unassigned + otherAlerts.length;

  const LEVEL_BADGE = {
    critical: "bg-red-50 text-red-600",
    attention: "bg-amber-50 text-amber-700",
    upcoming: "bg-miles-ice text-miles",
  };
  const LEVEL_LABEL = { critical: "Critique", attention: "Attention", upcoming: "À planifier" };

  const opsActivity = activity.filter((e) => !isAuditEvent(e));

  return (
    <div className="space-y-5" data-testid="admin-overview">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className={T.pageTitle} data-testid="overview-greeting">{greeting}{firstName ? `, ${firstName}` : ""}</h1>
          <p className="mt-1 text-sm capitalize text-slate">{dateLine}</p>
          <p className="mt-1 text-[13px] text-slate" data-testid="overview-context">
            <span className="font-medium text-ink">{today.rides} trajet{today.rides > 1 ? "s" : ""} aujourd&rsquo;hui</span>
            {" · "}{today.in_progress} en cours · {today.unassigned} à attribuer
          </p>
        </div>
        <div className={`flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-medium ${
          criticalCount > 0 ? "bg-red-50 text-red-600" : all.length > 0 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}
          data-testid="ops-health">
          <span className={`h-2 w-2 rounded-full ${criticalCount > 0 ? "bg-red-500" : all.length > 0 ? "bg-amber-500" : "bg-emerald-500"}`} />
          {criticalCount > 0 ? `${criticalCount} action${criticalCount > 1 ? "s" : ""} urgente${criticalCount > 1 ? "s" : ""}`
            : all.length > 0 ? `${all.length} point${all.length > 1 ? "s" : ""} à suivre` : "Opérations nominales"}
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="overview-kpis">
        {[
          ["Trajets aujourd'hui", today.rides, null],
          ["En cours", today.in_progress, null],
          ["À attribuer", today.unassigned, today.unassigned > 0 ? "text-amber-600" : null],
          ["CA du jour", money(finance.revenue_today), null],
        ].map(([label, value, tone]) => (
          <div key={label} className={`${T.card} p-4`}>
            <p className={`font-mono text-[22px] font-semibold tracking-tight ${tone || "text-ink"}`}>{value}</p>
            <p className="mt-0.5 text-xs text-slate">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-8">
          <Panel title="Nécessite une action" testId="overview-alerts"
            action={all.length > 5 && (
              <button type="button" onClick={() => setShowAllAlerts(!showAllAlerts)} className="text-xs font-medium text-miles hover:underline" data-testid="alerts-toggle">
                {showAllAlerts ? "Réduire" : `Tout voir (${attentionTotal})`}
              </button>
            )}>
            {all.length === 0 && <EmptyState title="Tout est sous contrôle" hint="Aucune action requise pour le moment." testId="alerts-empty" />}
            <div className="space-y-2">
              {visible.map((item, i) => {
                if (item.kind === "unassigned") {
                  const b = item.booking;
                  return (
                    <div key={b.id} className="flex items-center gap-4 rounded-xl bg-ink/[0.025] p-3.5 transition-colors hover:bg-miles-ice/40" data-testid={`alert-ride-${b.tracking_code}`}>
                      <div className="w-14 shrink-0">
                        <p className="font-mono text-[15px] font-semibold text-ink">{b.time}</p>
                        <p className={T.meta}>{b.date?.slice(5).split("-").reverse().join(".")}</p>
                      </div>
                      <div className="min-w-0 flex-1">
                        <RouteLine compact pickup={b.pickup} dropoff={b.dropoff} />
                        <p className={`mt-1 text-xs ${item.level === "critical" ? "font-semibold text-red-600" : "text-slate"}`}>
                          {b.vehicle_category} · {b.tracking_code}{item.min !== null && ` · ${departureLabel(item.min)}`}
                        </p>
                      </div>
                      <span className={`hidden rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider sm:inline ${LEVEL_BADGE[item.level]}`}>{LEVEL_LABEL[item.level]}</span>
                      <Link to={`/admin/app/bookings/${b.id}`} data-testid={`assign-cta-${b.tracking_code}`}
                        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-miles px-3 text-[13px] font-semibold text-white transition-colors hover:bg-miles-dark">
                        Attribuer <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                      </Link>
                    </div>
                  );
                }
                const a = item.alert;
                return (
                  <Link key={`alert-${i}`} to={a.booking_id ? `/admin/app/bookings/${a.booking_id}` : "#"} data-testid={`alert-${a.type}-${i}`}
                    className="flex items-center gap-3 rounded-xl bg-ink/[0.025] px-3.5 py-3 text-sm transition-colors hover:bg-miles-ice/40">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${LEVEL_BADGE[item.level]}`}>{LEVEL_LABEL[item.level]}</span>
                    <span className="flex-1 text-ink">{a.message}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-slate" strokeWidth={1.5} />
                  </Link>
                );
              })}
            </div>
          </Panel>

          <Panel title="Prochains départs" testId="overview-upcoming"
            action={<Link to="/admin/app/bookings?view=upcoming" className="text-xs font-medium text-miles hover:underline">Tout voir</Link>}>
            {upcoming.length === 0 && <EmptyState title="Aucun départ planifié" hint="Le planning est libre." testId="upcoming-empty" />}
            <ol className="space-y-0">
              {upcoming.slice(0, 6).map((b) => (
                <li key={b.id}>
                  <Link to={`/admin/app/bookings/${b.id}`} data-testid={`upcoming-${b.tracking_code}`}
                    className="flex items-center gap-4 rounded-xl px-2 py-2.5 transition-colors hover:bg-miles-ice/40">
                    <div className="w-14 shrink-0">
                      <p className="font-mono text-[15px] font-semibold text-ink">{b.time}</p>
                      <p className={T.meta}>{b.date?.slice(5).split("-").reverse().join(".")}</p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <RouteLine compact pickup={b.pickup} dropoff={b.dropoff} />
                      <p className="mt-0.5 text-xs text-slate">{b.vehicle_category} · {b.driver?.first_name ? b.driver.first_name : "chauffeur requis"}</p>
                    </div>
                    <StatusBadge status={b.display_status} />
                  </Link>
                </li>
              ))}
            </ol>
          </Panel>
        </div>

        <div className="space-y-4 lg:col-span-4">
          <Panel title="Finance" testId="overview-finance">
            <div className="space-y-1 text-sm">
              <div className="flex items-baseline justify-between py-1"><span className="text-slate">CA ce mois</span><span className="font-mono text-lg font-semibold text-ink" data-testid="finance-month">{money(finance.revenue_month)}</span></div>
              <div className="flex justify-between py-1"><span className="text-slate">Marge brute</span>{finance.has_costs ? <span className="font-mono font-semibold text-emerald-600" data-testid="finance-margin">{money(finance.gross_margin_month)}</span> : <span className="font-mono text-slate" data-testid="finance-margin" title="Renseignez les coûts sur les fiches réservation">—</span>}</div>
              <div className="flex justify-between py-1"><span className="text-slate">Panier moyen</span><span className="font-mono text-ink">{money(finance.avg_basket_month)}</span></div>
              <div className="flex justify-between py-1"><span className="text-slate">Paiements en attente</span><span className="font-mono text-amber-600">{finance.pending_payments}</span></div>
              <div className="flex justify-between py-1"><span className="text-slate">Factures échues</span><span className="font-mono text-ink">{finance.overdue_invoices}</span></div>
            </div>
            <Link to="/admin/app/finance" className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-miles hover:underline">
              Pilotage financier <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </Panel>

          <Panel title="Activité" testId="overview-activity">
            {opsActivity.length === 0 && <EmptyState title="Aucune activité" hint="Les événements opérationnels apparaîtront ici." />}
            <ol className="space-y-0">
              {opsActivity.slice(0, 10).map((e) => {
                const h = humanActivity(e);
                return (
                  <li key={e.id} className="flex gap-3 py-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-miles/60" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink">{h.title}</p>
                      <p className="truncate text-xs text-slate">
                        {[h.detail, e.tracking_code, h.who, timeAgo(e.at)].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </Panel>

          {/* Abandons : donnée conservée (tunnel de conversion), jamais une action */}
          {abandoned?.count > 0 && (
            <div className="rounded-2xl border border-dashed border-ink/[0.12] bg-white/60 p-5" data-testid="overview-abandoned">
              <button type="button" onClick={() => setShowAbandoned(!showAbandoned)} data-testid="abandoned-toggle"
                className="flex w-full items-center justify-between text-left">
                <span className="text-sm font-medium text-slate">
                  Réservations abandonnées <span className="ml-1 rounded-full bg-ink/[0.06] px-2 py-0.5 font-mono text-[11px] text-slate">{abandoned.count}</span>
                </span>
                <span className="text-xs text-slate/70">{showAbandoned ? "Masquer" : "Voir"}</span>
              </button>
              <p className="mt-1 text-xs text-slate/70">Paiement échoué ou interrompu par le client — aucune course n'a été confirmée.</p>
              {showAbandoned && (
                <ol className="mt-3 space-y-1.5">
                  {abandoned.recent.map((a) => (
                    <li key={a.booking_id}>
                      <Link to={`/admin/app/bookings/${a.booking_id}`} data-testid={`abandoned-${a.tracking_code}`}
                        className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-xs transition-colors hover:bg-miles-ice/50">
                        <span className="truncate text-slate"><span className="font-mono font-medium text-ink">{a.tracking_code}</span> · {a.label}</span>
                        <span className="shrink-0 font-mono text-slate">{money(a.price_chf)}</span>
                      </Link>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
